import Close from '@/assets/close.svg';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useId } from 'react';
import { isColorDark, fixColor } from '@/utils';
import { ModalPortal, ResponsiveImage } from '..';
import UmbracoForm from './UmbracoForm';

const DEFAULT_BACKGROUND = 'ded9d3'; // "Tan" from the Extended Approved Colour Picker

// Static class strings so Tailwind picks them up
const IMAGE_POSITION_CLASSES = {
  center: '[&_img]:object-center',
  top: '[&_img]:object-top',
  bottom: '[&_img]:object-bottom',
  left: '[&_img]:object-left',
  right: '[&_img]:object-right',
};

// Form layout, modal only (UmbracoForm and other forms unchanged):
// - text-black resets the inherited colour so inputs stay readable on any background
// - 1rem gaps between field rows / columns, more space above the button
const FORM_LAYOUT_CLASSES = [
  'w-full text-left text-black',
  '[&_.fieldset]:gap-4 [&_.fieldsets-container]:gap-4',
  '[&_.label]:text-small-paragraph',
  '[&_.buttons-container]:mt-6 [&_.buttons-container]:justify-center',
];

const SUBMIT_BUTTON_CLASSES = [
  '[&_.buttons-container_.btn]:w-full',
  '[&_.buttons-container_.btn]:border-bright-orange [&_.buttons-container_.btn]:bg-bright-orange',
  '[&_.buttons-container_.btn:hover]:border-deep-blue [&_.buttons-container_.btn:hover]:bg-deep-blue',
  // Button text white (only visible when disabled, when the span isn't transparent)
  '[&_.buttons-container_.btn]:text-white',
  // Animated label copies: the theme sets color: initial (black), override to white
  '[&_.buttons-container_.btn_span::before]:text-white',
  '[&_.buttons-container_.btn_span::after]:text-white',
];

// Internal page links must be app paths for router.push; strip the domain if one slipped through
const toAppPath = (url) => {
  if (!url || url.startsWith('/')) return url;
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

const GatedFormModal = ({ data, isOpen, onClose }) => {
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!data?.formFields) return null;

  const redirect =
    data.redirect?.url && data.redirect.type === 'content'
      ? { ...data.redirect, url: toAppPath(data.redirect.url) }
      : data.redirect;

  // Only treat the form-level redirect as real when it has a path. Some form definitions return an
  // empty route object, which would otherwise count as a redirect and navigate nowhere.
  const formLevelRoute = data.formFields.gotoPageOnSubmitRoute?.path ? data.formFields.gotoPageOnSubmitRoute : null;
  const formDefinition = { ...data.formFields, gotoPageOnSubmitRoute: formLevelRoute };

  const willRedirect = Boolean(redirect?.url || formLevelRoute);

  const backgroundColour = fixColor(data.backgroundColour || DEFAULT_BACKGROUND);
  const hasImage = Boolean(data.backgroundImage);
  const overlayOpacity = hasImage ? Number(data.overlayOpacity) || 0 : 0;

  // With an image, the colour underneath isn't visible, so text is always white (overlay controls legibility).
  // Without an image, the background colour decides.
  const isDark = hasImage || isColorDark(backgroundColour);
  const imagePositionClass = IMAGE_POSITION_CLASSES[data.backgroundPosition] || IMAGE_POSITION_CLASSES.center;

  const handleSubmitSuccessful = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gated_resource_unlock',
        resource_name: redirect?.resourceName,
        resource_url: redirect?.url,
        property_interest: data.propertyInterest,
      });
    }
    // Close only when navigating away; otherwise stay open to show the form's success message
    if (willRedirect) onClose();
  };

  return (
    <ModalPortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={{
              initial: { opacity: 0 },
              active: { opacity: 1 },
              exit: { opacity: 0 },
            }}
            initial="initial"
            animate="active"
            exit="exit"
            transition={{ duration: 0.4 }}
            className="disable-scroll fixed inset-0 z-[51] flex h-screen w-full items-center bg-black/75 p-6 pb-[21px] supports-[height:100dvh]:h-dvh md:px-10"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            {/* Outer panel: no overflow clipping, so the close button can sit on the corner */}
            <motion.div
              variants={{
                initial: { opacity: 0, y: 50 },
                active: { opacity: 1, y: 0 },
                exit: { opacity: 0 },
              }}
              transition={{ type: 'spring', damping: 20, stiffness: 120, delay: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              className="relative m-auto flex max-h-full w-full max-w-[600px] flex-col"
            >
              <button
                type="button"
                aria-label="Close"
                className="absolute right-0 top-0 z-20 flex size-8 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-white/80 bg-black transition-colors duration-300 hover:bg-deep-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:size-10 [&_path]:fill-white"
                onClick={onClose}
              >
                <Close role="presentation" viewBox="0 0 12 12" width="14" height="14" />
              </button>

              {/* Inner scroll container */}
              <div
                data-lenis-prevent
                className="person-modal-scrollbar min-h-0 w-full overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-black/5 scrollbar-thumb-black/25"
              >
                <div className="relative" style={{ backgroundColor: backgroundColour }}>
                  {hasImage && (
                    <div
                      aria-hidden="true"
                      className={classNames(
                        'pointer-events-none absolute inset-0 overflow-hidden [&>*]:size-full [&_img]:size-full [&_img]:object-cover',
                        imagePositionClass,
                      )}
                    >
                      <ResponsiveImage
                        image={data.backgroundImage}
                        widths={{ xs: 400, sm: 600, md: 600, lg: 600, xl: 600, xxl: 600 }}
                        heights={{ xs: 720, sm: 720, md: 720, lg: 720, xl: 720, xxl: 720 }}
                      />
                    </div>
                  )}

                  {overlayOpacity > 0 && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 bg-black"
                      style={{ opacity: overlayOpacity / 100 }}
                    />
                  )}

                  <div
                    className={classNames(
                      'relative flex flex-col items-center gap-4 px-m pb-6 pt-8 text-center md:gap-6 md:px-16 md:py-12',
                      isDark ? 'text-white' : 'text-black',
                    )}
                  >
                    <div className="heading-section flex flex-col gap-3">
                      {data.title && (
                        <h2
                          id={titleId}
                          className="font-larken text-2xl font-light leading-7 md:text-desktop-medium md:leading-desktop-medium"
                        >
                          {data.title}
                        </h2>
                      )}
                      {data.intro && (
                        <p
                          className={classNames(
                            'font-gotham text-paragraph font-s-light leading-6',
                            isDark ? 'text-white/75' : 'text-black/75',
                          )}
                        >
                          {data.intro}
                        </p>
                      )}
                    </div>

                    <UmbracoForm
                      className={classNames(
                        FORM_LAYOUT_CLASSES,
                        SUBMIT_BUTTON_CLASSES,
                        isDark ? '[&_.label]:text-white' : '[&_.label]:text-black',
                      )}
                      successClass={classNames(
                        'text-center font-gotham text-paragraph font-n-light',
                        isDark ? 'text-white' : 'text-black',
                      )}
                      formDefinition={formDefinition}
                      submitLabelOverride={data.submitLabel}
                      redirectOverride={redirect}
                      hiddenValues={{
                        propInterest: data.propertyInterest,
                        resourceName: redirect?.resourceName,
                        resourceUrl: redirect?.url,
                      }}
                      onSubmitSuccessful={handleSubmitSuccessful}
                    />

                    {data.privacyText && (
                      <div
                        className={classNames(
                          'leading-small-paragraph py-2 font-gotham text-small-paragraph font-n-light md:py-0 [&_a]:underline [&_p:empty]:hidden',
                          isDark ? 'text-white/60' : 'text-black/50',
                        )}
                        dangerouslySetInnerHTML={{ __html: data.privacyText.replaceAll('<p>&nbsp;</p>', '') }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ModalPortal>
  );
};

export default GatedFormModal;
